import React, { useEffect, useRef, useState } from 'react';
import styles from './windows-xp-desktop.module.scss';
import WindowsXPModal from '../../../components/windows-xp-modal/windows-xp-modal.component';
import {
  classnames,
  debounce,
} from '../../../common/general-utilities/general-utilities';
import { windowsWallpaperAvatar } from '../../../common/constant/interactive.resume.constant';
import { IDefaultResponsiveSupport } from '../../../common/interfaces/common.interface';
import StartMenu from '../start-menu-content/start-menu-content';
import { WindowItem } from '../../../common/interfaces/os-resume-interface';
import MobileTaskGroup from '../mobile-task-group/mobile-task-group';
import WindowsXPSplash from '../../../components/windows-xp-splash/windows-xp-splash.component';
import { useHistory } from 'react-router-dom';
import { useSystemSounds } from '../../../hooks/system-sounds/useSystemSounds';
import ResponsiveModeService from '../../../common/services/responsive-mode.service';

const MAX_WINDOW_OPEN_LIMIT = 5;

interface WindowsXPDesktopProps extends IDefaultResponsiveSupport {
  availableWindowsModals: Map<string, WindowItem>;
}

const WindowsXPDesktop: React.FC<WindowsXPDesktopProps> = ({
  responsiveMode,
  availableWindowsModals,
}) => {
  const startMenuRef = useRef<HTMLDivElement>(null);
  const startButtonRef = useRef<HTMLButtonElement>(null);
  const [activeWindows, setWindows] = useState<Map<string, WindowItem>>(
    () => new Map(),
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const [startOpen, setStartOpen] = useState(false);
  const [shutdown, setShutdown] = useState(false);
  const [time, setTime] = useState(new Date());
  const zIndexCounter = useRef(1);
  const history = useHistory();
  const { play, mute, isMuted } = useSystemSounds();
  /* ------------------ CLOCK UPDATE ------------------ */
  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!startOpen) return;

      const target = event.target as Node;

      if (
        startMenuRef.current &&
        !startMenuRef.current.contains(target) &&
        startButtonRef.current &&
        !startButtonRef.current.contains(target)
      ) {
        setStartOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [startOpen]);

  /* ------------------ WINDOW MANAGEMENT ------------------ */

  const openWindow = (id: string) => {
    play('click');
    setWindows((prev) => {
      const next = new Map(prev);
      next.set(id, {
        ...availableWindowsModals.get(id)!,
        zIndex: ++zIndexCounter.current,
      });
      return next;
    });
    setActiveId(id);
    setStartOpen(false);
  };
  const debouncedOpenWindow = debounce(openWindow, 100);

  const closeWindow = (id: string) => {
    setWindows((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    if (activeId === id) {
      setActiveId(null);
    }
  };

  const toggleMinimize = (id: string) => {
    setWindows((prev) => {
      const next = new Map(prev);
      const win = next.get(id);
      win && next.set(id, { ...win, minimized: !win?.minimized! });
      return next;
    });
    if (activeId === id) {
      setActiveId(null);
    }
  };

  const activateWindow = (id: string) => {
    setWindows((prev) => {
      const next = new Map(prev);
      const win = next.get(id);
      win &&
        next.set(id, {
          ...win,
          minimized: false,
          zIndex: ++zIndexCounter.current,
        });
      return next;
    });

    setActiveId(id);
  };

  const handleStartBtnClick = () => {
    play('click');
    setStartOpen((prev) => !prev);
  };

  const handleToolBarWindow = (id: string, minimized: boolean) => {
    play('click');
    minimized
      ? activateWindow(id)
      : activeId === id
        ? toggleMinimize(id)
        : activateWindow(id);
  };

  const handleShutdown = () => {
    setStartOpen(false);
    setShutdown(true);
    play('shutdown');
    setTimeout(() => history.push('/'), 2000);
  };

  /* ------------------ SHUTDOWN SCREEN ------------------ */

  if (shutdown) {
    return <WindowsXPSplash />;
  }

  /* ------------------ DESKTOP ------------------ */

  return (
    <div
      className={classnames(
        styles.desktop,
        ResponsiveModeService.responsiveModeClass(styles, responsiveMode),
      )}
      style={{ backgroundImage: windowsWallpaperAvatar }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* -------- Desktop Icons -------- */}
      <div className={styles.icons}>
        {[...(availableWindowsModals?.values() || [])].map((win) => (
          <button
            onClick={() => debouncedOpenWindow(win.id)}
            className={styles.icon}
            key={win.id}
          >
            {win.Icon}
            <span>{win.title}</span>
          </button>
        ))}
      </div>

      {/* -------- Windows -------- */}
      {[...(activeWindows.values() || [])].map((win) => (
        <WindowsXPModal
          key={win.id}
          isOpen
          minimized={win?.minimized}
          isActive={activeId === win.id}
          title={win.title}
          Icon={win.Icon}
          onActivate={() => activateWindow(win.id)}
          onMinimize={() => toggleMinimize(win.id)}
          onClose={() => closeWindow(win.id)}
          openWindowsCount={activeWindows.size}
          zIndex={win.zIndex!}
          responsiveMode={responsiveMode}
          play={play}
          width={win.width}
          height={win.height}
          isOverflowHidden={win.isOverflowHidden}
          handleShutdown={handleShutdown}
        >
          {availableWindowsModals?.get(win?.id)?.Component ||
            'Unable to fetch the record!'}
        </WindowsXPModal>
      ))}

      {/* -------- Taskbar -------- */}
      <div className={styles.taskbar}>
        {/* START BUTTON */}
        <button
          ref={startButtonRef}
          className={styles.startButton}
          onClick={handleStartBtnClick}
        >
          <span>start</span>
        </button>

        {/* OPEN WINDOWS */}
        <div className={styles.taskArea}>
          {responsiveMode === 'desktop' &&
            activeWindows.size <= MAX_WINDOW_OPEN_LIMIT &&
            [...(activeWindows.values() || [])].map((w) => (
              <div
                key={w.id}
                onClick={() => handleToolBarWindow(w.id, w.minimized!)}
                className={classnames(styles.taskItem, {
                  [styles.activeTask]: activeId === w.id && !w.minimized,
                })}
              >
                {w.Icon}
                <span className={styles.taskTitle}>{w.title}</span>
              </div>
            ))}

          {(responsiveMode !== 'desktop' ||
            activeWindows.size > MAX_WINDOW_OPEN_LIMIT) &&
            activeWindows.size > 0 && (
              <MobileTaskGroup
                windows={[...activeWindows.values()]}
                activeId={activeId}
                activateWindow={activateWindow}
                closeWindow={closeWindow}
                play={play}
              />
            )}
        </div>

        <div className={styles.tray}>
          <span
            onClick={() => mute(!isMuted)}
            className={styles.mute}
            role="button"
          >
            {isMuted ? <span>&#128263;</span> : <span>&#128266;</span>}
          </span>
          <span className={styles.clock}>
            {time.toLocaleTimeString([], {
              hour: 'numeric',
              minute: 'numeric',
              hour12: true,
            })}
          </span>
        </div>
      </div>

      {/* -------- Start Menu -------- */}
      {startOpen && (
        <StartMenu
          ref={startMenuRef}
          openWindow={debouncedOpenWindow}
          availableWindowsModals={availableWindowsModals}
          onShutdown={handleShutdown}
        />
      )}
    </div>
  );
};

export default WindowsXPDesktop;
